import api from './config';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';

const normalizeErrors = (message) =>
  Array.isArray(message) ? message : message ? [message] : [];

const initialState = {
  claims: [],
  archivedClaims: [],
  claim: null,
  loading: false,
  errors: [],
  recordsTotal: 0,
  statusCounts: { open: 0, in_progress: 0, treated: 0, closed: 0 },
  dashboardStats: null,
  statsLoading: false,
  statsError: null,
  // Messenger sidebar (claimMessanger): paginated, lightweight rows from
  // GET /claim?view=list - kept apart from `claims` (used by the tables).
  messenger: { items: [], total: 0, loading: false, loadingMore: false, key: null }
};

export const MESSENGER_PAGE_SIZE = 20;

export const fetchClaims = createAsyncThunk(
  'claims/fetchClaims',
  async (params, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/claim', { params });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// Cheap per-status totals for the tab badges: reuses the paginated /claim
// endpoint with length:1 so it only returns a single row, not the whole
// list - length:0 makes the backend send Mongo an invalid $limit:0 and
// throw ("the limit must be positive"). We only read recordsFiltered
// anyway, which is computed by a separate unlimited aggregation.
export const fetchClaimStatusCounts = createAsyncThunk(
  'claims/fetchClaimStatusCounts',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const statuses = ['open', 'in_progress', 'treated', 'closed'];
      const responses = await Promise.all(
        statuses.map((status) =>
          api.get('/claim', { params: { length: 1, filters: { status } } })
        )
      );
      return statuses.reduce((acc, status, i) => {
        acc[status] = responses[i].data?.recordsFiltered || 0;
        return acc;
      }, {});
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

// One page of the messenger sidebar. `mode`:
//  - 'reset'   page 1 for a new tab/search (replaces the list)
//  - 'append'  next page on scroll (`start` = rows already loaded)
//  - 'refresh' page 1 again after a new message: those rows move to the top,
//              the already-loaded rows below are kept (minus `dropId`)
// `key` identifies the tab+search the request was made for, so a late
// response for a previous tab/search is dropped instead of mixed in.
export const fetchMessengerClaims = createAsyncThunk(
  'claims/fetchMessengerClaims',
  async (
    { status, search, includeAnonymous, start = 0 },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const params = { view: 'list', start, length: MESSENGER_PAGE_SIZE };
      if (status && status !== 'all') params.filters = { status };
      if (search) params.search = { value: search };
      if (includeAnonymous) params.includeAnonymous = 'true';
      const response = await api.get('/claim', { params });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchClaimByID = createAsyncThunk(
  'claims/fetchClaimByID',
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/claim/getDetailClaims/${id}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const addMessage = createAsyncThunk(
  'claims/addMessage',
  async ({ id, message }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/claim/${id}/add-message`, { message });
      await dispatch(fetchClaimByID(id));
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const addMessagetreated = createAsyncThunk(
  'claims/addMessagetreated',
  async ({ id, message }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/claim/${id}/add_message_treated`, { message });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const AddMediaMessage = createAsyncThunk(
  'claims/AddMediaMessage',
  async ({ formData, id }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/claim/${id}/add-files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      dispatch(fetchClaimByID(id));
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data);
    }
  }
);

export const updateStatusClaim = createAsyncThunk(
  'claims/updateStatusClaim',
  async ({ id, status }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/claim/${id}/${status}/update-status`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchArchivedClaimsByCompany = createAsyncThunk(
  'claims/fetchArchivedClaimsByCompany',
  async (companyId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/claim/archived/company/${companyId}`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const unarchive = createAsyncThunk(
  'claims/unarchive',
  async (claimId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/claim/${claimId}/unarchive`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const archive = createAsyncThunk(
  'claims/archive',
  async (claimId, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/claim/${claimId}/archive`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const fetchClaimBarchivedAll = createAsyncThunk(
  'claims/fetchClaimBarchivedAll',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/claim/archivedAll/');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const blockClient = createAsyncThunk(
  'claims/blockClient',
  async (claim, { rejectWithValue, dispatch }) => {
    try {
      await api.put(`/user/block/${claim.added_by[0]?._id}`);
      const result = await dispatch(fetchClaimByID(claim?._id));
      return result.payload;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const unblockClient = createAsyncThunk(
  'claims/unblockClient',
  async (claim, { rejectWithValue, dispatch }) => {
    try {
      await api.put(`/user/unblock/${claim.added_by[0]?._id}`);
      const result = await dispatch(fetchClaimByID(claim?._id));
      return result.payload;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteMessage = createAsyncThunk(
  'claims/deleteMessage',
  async ({ claimId, actionId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.delete(`/claim/${claimId}/delete/${actionId}`);
      dispatch(fetchClaimByID(claimId));
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || 'Error deleting message');
    }
  }
);

export const markActionsViewed = createAsyncThunk(
  'claims/markActionsViewed',
  async (claimId, { rejectWithValue, dispatch }) => {
    try {
      await api.patch(`/claim/${claimId}/actions-viewed`);
      const { payload } = await dispatch(fetchClaimByID(claimId));
      return payload;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || 'Server Error');
    }
  }
);

// Opening a conversation: the backend marks the client's messages as seen and
// resets the company's unread counter. Unlike markActionsViewed, no loading
// flag and no claim refetch - the counter is zeroed optimistically in the
// list, and the sidebar menu refetches its total on 'claims:unreadChanged'.
export const markClaimMessagesSeen = createAsyncThunk(
  'claims/markClaimMessagesSeen',
  async (claimId, { rejectWithValue, dispatch }) => {
    try {
      await api.patch(`/claim/${claimId}/actions-viewed`);
      window.dispatchEvent(new Event('claims:unreadChanged'));
      return claimId;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || 'Server Error');
    }
  }
);

export const fetchDashboardStats = createAsyncThunk(
  'claims/fetchDashboardStats',
  async ({ startDate, endDate } = {}, { rejectWithValue, dispatch }) => {
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await api.get('/stats/dashboard', { params });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

const claimsSlice = createSlice({
  name: 'claims',
  initialState,
  reducers: {
    appendMessageToClaim: (state, action) => {
      if (!state.claim) return;
      state.claim.actions.push(action.payload);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClaims.pending, (state) => { state.loading = true; })
      .addCase(fetchClaims.fulfilled, (state, { payload }) => {
        state.claims = payload?.data || [];
        state.recordsTotal = payload?.recordsTotal || 0;
        state.loading = false;
      })
      .addCase(fetchClaims.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(fetchMessengerClaims.pending, (state, { meta }) => {
        const { mode, key } = meta.arg;
        if (mode === 'reset') {
          state.messenger.key = key;
          state.messenger.loading = true;
        } else if (mode === 'append') {
          state.messenger.loadingMore = true;
        }
      })
      .addCase(fetchMessengerClaims.fulfilled, (state, { payload, meta }) => {
        const { mode, key } = meta.arg;
        const m = state.messenger;
        if (key !== m.key) return;
        const rows = payload?.data || [];
        if (mode === 'reset') {
          m.items = rows;
          m.loading = false;
        } else {
          const ids = new Set(rows.map((r) => r.DT_RowId));
          // `dropId`: a claim we just changed ourselves (e.g. marked treated)
          // - if page 1 doesn't bring it back, it left this tab.
          const others = m.items.filter(
            (r) => !ids.has(r.DT_RowId) && r.DT_RowId !== meta.arg.dropId
          );
          m.items = mode === 'append' ? [...others, ...rows] : [...rows, ...others];
          if (mode === 'append') m.loadingMore = false;
        }
        m.total = payload?.recordsFiltered || 0;
      })
      .addCase(fetchMessengerClaims.rejected, (state, { meta }) => {
        if (meta.arg.key !== state.messenger.key) return;
        state.messenger.loading = false;
        state.messenger.loadingMore = false;
      })

      .addCase(fetchClaimStatusCounts.fulfilled, (state, { payload }) => {
        state.statusCounts = payload;
      })

      .addCase(fetchClaimByID.pending, (state) => { state.loading = true; })
      .addCase(fetchClaimByID.fulfilled, (state, { payload }) => {
        state.claim = payload;
        state.loading = false;
      })
      .addCase(fetchClaimByID.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(addMessage.pending, (state) => { state.loading = true; })
      .addCase(addMessage.fulfilled, (state) => { state.loading = false; })
      .addCase(addMessage.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(addMessagetreated.pending, (state) => { state.loading = true; })
      .addCase(addMessagetreated.fulfilled, (state, { payload }) => {
        state.loading = false;
        if (state.claim && payload?._id && state.claim._id === payload._id) {
          state.claim = payload;
        }
      })
      .addCase(addMessagetreated.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(AddMediaMessage.pending, (state) => { state.loading = true; })
      .addCase(AddMediaMessage.fulfilled, (state) => { state.loading = false; })
      .addCase(AddMediaMessage.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(updateStatusClaim.pending, (state) => { state.loading = true; })
      .addCase(updateStatusClaim.fulfilled, (state) => { state.loading = false; })
      .addCase(updateStatusClaim.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(archive.pending, (state) => { state.loading = true; })
      .addCase(archive.fulfilled, (state) => { state.loading = false; })
      .addCase(archive.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(unarchive.pending, (state) => { state.loading = true; })
      .addCase(unarchive.fulfilled, (state) => { state.loading = false; })
      .addCase(unarchive.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(fetchArchivedClaimsByCompany.pending, (state) => { state.loading = true; })
      .addCase(fetchArchivedClaimsByCompany.fulfilled, (state, { payload }) => {
        state.archivedClaims = payload;
        state.loading = false;
      })
      .addCase(fetchArchivedClaimsByCompany.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(blockClient.pending, (state) => { state.loading = true; })
      .addCase(blockClient.fulfilled, (state, { payload }) => {
        if (payload) state.claim = payload;
        state.loading = false;
      })
      .addCase(blockClient.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(unblockClient.pending, (state) => { state.loading = true; })
      .addCase(unblockClient.fulfilled, (state, { payload }) => {
        if (payload) state.claim = payload;
        state.loading = false;
      })
      .addCase(unblockClient.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(deleteMessage.pending, (state) => { state.loading = true; })
      .addCase(deleteMessage.fulfilled, (state) => { state.loading = false; })
      .addCase(deleteMessage.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(markClaimMessagesSeen.pending, (state, { meta }) => {
        const claim = state.claims.find((c) => c.DT_RowId === meta.arg);
        if (claim) claim.companyUnreadCount = 0;
        const row = state.messenger.items.find((c) => c.DT_RowId === meta.arg);
        if (row) row.companyUnreadCount = 0;
      })

      .addCase(markActionsViewed.pending, (state) => { state.loading = true; })
      .addCase(markActionsViewed.fulfilled, (state, { payload }) => {
        state.claim = payload;
        state.loading = false;
      })
      .addCase(markActionsViewed.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(fetchClaimBarchivedAll.pending, (state) => { state.loading = true; })
      .addCase(fetchClaimBarchivedAll.fulfilled, (state, { payload }) => {
        state.archivedClaims = payload;
        state.loading = false;
      })
      .addCase(fetchClaimBarchivedAll.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(fetchDashboardStats.pending, (state) => {
        state.statsLoading = true;
        state.statsError = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, { payload }) => {
        state.statsLoading = false;
        state.dashboardStats = payload;
        state.statsError = null;
      })
      .addCase(fetchDashboardStats.rejected, (state, { payload }) => {
        state.statsLoading = false;
        state.statsError = payload?.message || 'Failed to fetch dashboard stats';
        state.dashboardStats = null;
      });
  }
});

export const { appendMessageToClaim } = claimsSlice.actions;

export default claimsSlice.reducer;
